// Group-incoming ring — full-screen scrim, centered card, pulsing avatar
// of the initiator + a "N others" subtitle when the group has 3+ members.
// Card chrome, entrance animation, and pulse-ring keyframe are byte-for-byte
// the same as IncomingCallModal so the two read as one family.
// Auto-dismisses when callManager moves the state on (session ended, accept,
// decline, busy-block elsewhere). The label icon is Users to read as
// "group call" at a glance.

import { Phone, PhoneOff, Users } from 'lucide-react'
import Avatar from '@/components/Avatar'
import { useCallManager } from '@/lib/callManager'

export function GroupIncomingCallModal() {
  const { state, acceptGroupCall, declineGroupInvite } = useCallManager()
  if (state.kind !== 'group-incoming-ringing') return null

  // 2-person group reads as "Direct call"-style (just the initiator's name);
  // 3+ adds "and N others" — N is total participants minus me minus the
  // initiator (the count includes everyone in the conversation).
  const others = Math.max(0, state.participantCount - 2)
  const subtitle =
    others > 0
      ? `${state.initiatorName} and ${others} other${others === 1 ? '' : 's'}`
      : state.initiatorName

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Incoming group call from ${state.initiatorName}`}
      style={{
        position: 'fixed', inset: 0,
        background: 'var(--overlay-scrim)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 500, padding: 16,
      }}
    >
      <div
        className="call-card-enter"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 20, padding: 32,
          width: '100%', maxWidth: 380,
          boxShadow: 'var(--shadow-popover-mid)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18,
        }}
      >
        {/* Label */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          color: 'var(--ink-tertiary)',
        }}>
          <Users style={{ width: 12, height: 12 }} />
          <span style={{
            fontSize: 11, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>
            Incoming group call
          </span>
        </div>

        {/* Initiator avatar w/ pulsing ring */}
        <div style={{ position: 'relative', width: 88, height: 88, marginTop: 4 }}>
          <div
            className="call-pulse-ring"
            aria-hidden="true"
            style={{
              position: 'absolute', inset: -6, borderRadius: '50%',
              border: '2px solid var(--accent)',
              pointerEvents: 'none',
            }}
          />
          <div style={{
            width: 88, height: 88, borderRadius: '50%', overflow: 'hidden',
            position: 'relative',
          }}>
            <Avatar name={state.initiatorName} size={88} variant="contact" />
          </div>
        </div>

        {/* Initiator name + subtitle */}
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          textAlign: 'center', width: '100%',
        }}>
          <p style={{
            fontSize: 19, fontWeight: 700, color: 'var(--ink-primary)',
            margin: 0, letterSpacing: '-0.01em',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            width: '100%',
          }}>
            {state.initiatorName}
          </p>
          <p style={{
            fontSize: 13, color: 'var(--ink-secondary)', margin: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            width: '100%',
          }}>
            {subtitle}
          </p>
        </div>

        {/* Decline / Accept — identical pattern to IncomingCallModal */}
        <div style={{ display: 'flex', gap: 12, width: '100%', marginTop: 6 }}>
          <button
            type="button"
            onClick={declineGroupInvite}
            aria-label="Decline group call"
            style={{
              flex: 1, height: 48,
              background: 'var(--danger-dim)', color: 'var(--danger)',
              border: '1px solid var(--danger-border)', borderRadius: 12,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <PhoneOff style={{ width: 16, height: 16 }} /> Decline
          </button>
          <button
            type="button"
            onClick={acceptGroupCall}
            aria-label="Accept group call"
            style={{
              flex: 1, height: 48,
              background: 'var(--positive)', color: 'var(--ink-inverse)',
              border: 'none', borderRadius: 12,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Phone style={{ width: 16, height: 16 }} /> Accept
          </button>
        </div>
      </div>
    </div>
  )
}
